"use client";

import { Vehicle } from "@alba-cars/common-modules";
import { useTranslations, useLocale } from "next-intl";
import dynamic from "next/dynamic";
import Image from "next/image";
import React, { useState } from "react";

import IconArrowRight from "@/components/Icons/IconArrowRight";
import IconCheck from "@/components/Icons/IconCheck";
import inspectionReportPhoto from "@/public/assets/images/inspection-report.webp";
import inspectionReportArPhoto from "@/public/assets/images/inspection-ar-report.png";

const InspectionReportModal = dynamic(() => import("./InspectionReportModal"), {
  loading: () => (
    <div className="fixed inset-0 flex items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
    </div>
  ),
  ssr: false,
});

interface CarInspectionReportProps {
  car?: Vehicle | null;
}

const CarInspectionReport: React.FC<CarInspectionReportProps> = ({ car }) => {
  const t = useTranslations("Inspection.report");
  const locale = useLocale();
  const isRTL = locale === "ar";
  const [ isModalOpen, setIsModalOpen ] = useState( false );
   const inspectionImage = isRTL ? inspectionReportArPhoto : inspectionReportPhoto;

  const options = [t("exterior"), t("engine"), t("electricals"), t("suspension")];

  return (
    <>
      <div className="flex flex-col rounded-3xl bg-white p-4 lg:flex-row" id="inspection-report">
        <div className="relative aspect-square w-full xs:aspect-[4/3] lg:w-[100rem] 2xl:aspect-video 2xl:w-full">
          <Image
            fill
            priority
            quality={50}
            sizes="(max-width: 768px) 300px, (max-width: 1200px) 400px, 500px"
            src={inspectionImage}
            alt="inspection-report-photo"
            className="rounded-3xl object-cover"
          />
        </div>
        <div className="flex flex-col justify-center space-y-6 py-10 lg:px-8">
          <ul className="grid grid-cols-2 gap-4">
            {options.map((option, index) => (
              <li
                key={index}
                className={`flex items-center rounded-xl bg-secondary-light p-3 text-left font-inter font-medium text-black ${
                  isRTL ? "gap-3 space-x-reverse" : "gap-3"
                }`}
              >
                <div className="h-5 w-5 flex-shrink-0 rounded-full bg-success-lighter">
                  <IconCheck bg="transparent" fill="white" />
                </div>
                <span className="text-sm lg:text-base">{option}</span>
              </li>
            ))}
          </ul>
          <p className="font-inter text-sm font-normal text-black">{t("description")}</p>
          <button
            onClick={() => {
              setIsModalOpen(true);
            }}
            className="group flex cursor-pointer items-center font-inter text-base font-semibold text-primary"
            aria-expanded={isModalOpen}
            aria-label="view-full-report-button"
          >
            <span className={`group-hover:text-primary-dark ${isRTL ? "ml-1" : "mr-1"}`}>{t("viewFullReport")}</span>{" "}
            <IconArrowRight
              fill="red"
              className={`transform transition-transform duration-300 ${
                isRTL ? "rotate-180 group-hover:-translate-x-1" : "group-hover:translate-x-1"
              }`}
            />
          </button>
        </div>
      </div>
      {isModalOpen && car && (
        <InspectionReportModal
          car={car}
          onClose={() => {
            setIsModalOpen(false);
            const anchor = document.getElementById("inspection-report");
            if (anchor) {
              anchor.scrollIntoView({ behavior: "smooth" });
            }
          }}
        />
      )}
    </>
  );
};

export default CarInspectionReport;
